import { useState } from 'react';
import { Alert } from '@flintmint/web-shared/components/alert';
import { Button } from '@flintmint/web-shared/components/button';
import { Switch } from '@flintmint/web-shared/components/switch';

import type { FlowWithId, FlowInput } from '@/services/flows-service';
import { formatUpdatedInfo } from '@/utils/format-values';
import { usePromptSlugValidation, type PromptSlugErrors } from '../../use-prompt-slug-validation';
import { SlugField } from '../slug-field/slug-field';

import styles from './flow-row.module.scss';

interface FlowRowProps {
  flow: FlowWithId;
  moduleCount: number;
  onSave: (id: string, data: Partial<FlowInput>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

type FlowEditData = {
  name: string;
  criteria: string;
  moduleSelectionPromptSlug: string;
  corePromptSlug: string;
};

const toEditData = (flow: FlowWithId): FlowEditData => ({
  name: flow.name,
  criteria: flow.criteria,
  moduleSelectionPromptSlug: flow.moduleSelectionPromptSlug,
  corePromptSlug: flow.corePromptSlug,
});

export const FlowRow = ({ flow, moduleCount, onSave, onDelete }: FlowRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<FlowEditData>(toEditData(flow));
  const [slugErrors, setSlugErrors] = useState<PromptSlugErrors>({});
  const validatePromptSlugs = usePromptSlugValidation();

  const isValid =
    editData.name.trim() &&
    editData.criteria.trim() &&
    editData.moduleSelectionPromptSlug.trim() &&
    editData.corePromptSlug.trim();

  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      const moduleSelectionPromptSlug = editData.moduleSelectionPromptSlug.trim();
      const corePromptSlug = editData.corePromptSlug.trim();
      const errors = await validatePromptSlugs({ moduleSelectionPromptSlug, corePromptSlug });
      setSlugErrors(errors);
      if (Object.keys(errors).length > 0) return;

      await onSave(flow.id, {
        name: editData.name.trim(),
        criteria: editData.criteria.trim(),
        moduleSelectionPromptSlug,
        corePromptSlug,
      });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    setEditData(toEditData(flow));
    setSlugErrors({});
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditData(toEditData(flow));
    setIsEditing(false);
  };

  // Enabling is what makes a flow's prompts load, so it is checked like a save. A missing slug opens the
  // editor with the errors instead of enabling; disabling can't activate anything and stays instant.
  const handleEnabledChange = async (enabled: boolean) => {
    if (isSaving) return;
    if (!enabled) {
      await onSave(flow.id, { enabled });
      return;
    }

    setIsSaving(true);
    try {
      const errors = await validatePromptSlugs({
        moduleSelectionPromptSlug: flow.moduleSelectionPromptSlug,
        corePromptSlug: flow.corePromptSlug,
      });
      if (Object.keys(errors).length > 0) {
        setEditData(toEditData(flow));
        setSlugErrors(errors);
        setIsEditing(true);
        return;
      }

      await onSave(flow.id, { enabled });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSlugChange = (field: keyof PromptSlugErrors, value: string) => {
    setEditData({ ...editData, [field]: value });
    setSlugErrors({ ...slugErrors, [field]: undefined });
  };

  if (isEditing) {
    return (
      <div className={styles.row} data-editing data-testid="flow-row" data-name={flow.name}>
        <input
          className={styles.nameInput}
          value={editData.name}
          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
          placeholder="Name"
          disabled={isSaving}
          data-testid="flow-name-input"
        />
        <textarea
          className={styles.criteriaTextarea}
          value={editData.criteria}
          onChange={(e) => setEditData({ ...editData, criteria: e.target.value })}
          placeholder="Routing criteria fed to the router"
          rows={3}
          disabled={isSaving}
          data-testid="flow-criteria-input"
        />
        <SlugField
          value={editData.moduleSelectionPromptSlug}
          onChange={(value) => handleSlugChange('moduleSelectionPromptSlug', value)}
          placeholder="Module selection prompt slug"
          disabled={isSaving}
          error={slugErrors.moduleSelectionPromptSlug}
          testId="flow-selection-slug"
        />
        <SlugField
          value={editData.corePromptSlug}
          onChange={(value) => handleSlugChange('corePromptSlug', value)}
          placeholder="Core prompt slug"
          disabled={isSaving}
          error={slugErrors.corePromptSlug}
          testId="flow-core-slug"
        />
        <span />
        <span />
        <span className={styles.updated} />
        <div className={styles.actions}>
          <Button
            size="small"
            color="success"
            onClick={handleSave}
            loading={isSaving}
            disabled={!isValid}
            appearance="outline"
            data-testid="flow-save-btn"
          >
            Save
          </Button>
          <Button size="small" onClick={handleCancel} disabled={isSaving} appearance="outline" data-testid="flow-cancel-btn">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.row} data-disabled={flow.enabled === false || undefined} data-testid="flow-row" data-name={flow.name}>
      <span className={styles.name}>{flow.name}</span>
      <span className={styles.criteria}>{flow.criteria || '—'}</span>
      <span className={styles.slug}>{flow.moduleSelectionPromptSlug}</span>
      <span className={styles.slug}>{flow.corePromptSlug}</span>
      <span className={styles.moduleCount} data-empty={moduleCount === 0 || undefined} data-testid="flow-module-count">
        {moduleCount}
      </span>
      <Switch checked={flow.enabled !== false} onChange={handleEnabledChange} data-testid="flow-enabled-toggle" />
      <span className={styles.updated}>{formatUpdatedInfo(flow.updatedAt, flow.updatedBy)}</span>
      <div className={styles.actions}>
        <Button size="small" onClick={handleEdit} appearance="outline" color="light" data-testid="flow-edit-btn">
          Edit
        </Button>
        {moduleCount > 0 ? (
          <Button
            size="small"
            color="danger"
            appearance="outline"
            disabled
            title={moduleCount === 1 ? '1 module references this flow' : `${moduleCount} modules reference this flow`}
            data-testid="flow-delete-btn"
          >
            Delete
          </Button>
        ) : (
          <Alert
            type="danger"
            title="Flow"
            message={`Are you sure you want to delete "${flow.name}"?`}
            primaryButton={{
              onClick: () => onDelete(flow.id),
              appearance: 'outline',
            }}
            secondaryButton={{
              appearance: 'outline',
            }}
            trigger={
              <Button size="small" color="danger" appearance="outline" data-testid="flow-delete-btn">
                Delete
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
};
